import { Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Music, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createSong,
  deleteSong,
  listSongs,
  type NewSongInput,
  type SongDto,
} from '../../lib/service-planning-api'

function NewSongForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<NewSongInput>({ title: '' })
  const [tagsText, setTagsText] = useState('')

  const create = useMutation({
    mutationFn: () =>
      createSong({
        title: form.title.trim(),
        author: form.author?.trim() || null,
        ccli: form.ccli?.trim() || null,
        defaultKey: form.defaultKey?.trim() || null,
        bpm: form.bpm ?? null,
        tags: tagsText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['songs'] })
      onClose()
    },
  })

  return (
    <Card className="space-y-4 p-5">
      <h2 className="font-display text-lg font-semibold tracking-tight">Add song</h2>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (form.title.trim()) create.mutate()
        }}
      >
        <label className="space-y-1.5 text-sm sm:col-span-2">
          <span className="text-muted">Title</span>
          <Input
            autoFocus
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="How Great Is Our God"
            required
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="text-muted">Author</span>
          <Input
            value={form.author ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
            placeholder="Chris Tomlin"
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="text-muted">CCLI #</span>
          <Input
            value={form.ccli ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, ccli: e.target.value }))}
            placeholder="4348399"
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="text-muted">Default key</span>
          <Input
            value={form.defaultKey ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, defaultKey: e.target.value }))}
            placeholder="C"
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="text-muted">BPM</span>
          <Input
            type="number"
            min={1}
            max={400}
            value={form.bpm ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, bpm: e.target.value ? Number(e.target.value) : null }))
            }
            placeholder="78"
          />
        </label>
        <label className="space-y-1.5 text-sm sm:col-span-2">
          <span className="text-muted">Tags (comma-separated)</span>
          <Input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="worship, fast, christmas"
          />
        </label>
        <div className="flex items-center gap-2 sm:col-span-2">
          <Button type="submit" disabled={create.isPending || !form.title.trim()}>
            {create.isPending ? <Spinner /> : <Plus />}
            Add song
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {create.isError && (
            <span className="text-sm text-danger">
              {create.error instanceof Error ? create.error.message : 'Could not add the song.'}
            </span>
          )}
        </div>
      </form>
    </Card>
  )
}

export function SongLibrary() {
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['songs'],
    queryFn: () => listSongs(),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteSong(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['songs'] }),
  })

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        (s.author ?? '').toLowerCase().includes(q) ||
        s.tags.some((tag) => tag.toLowerCase().includes(q)),
    )
  }, [data, search])

  return (
    <div className="space-y-6">
      <Link
        to="/app/service-planning"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Service plans
      </Link>

      <PageHeader
        title="Song Library"
        description="Your org’s reusable worship song catalog."
        actions={
          <Button onClick={() => setShowForm((s) => !s)}>
            <Plus />
            Add song
          </Button>
        }
      />

      {showForm && <NewSongForm onClose={() => setShowForm(false)} />}

      <Input
        placeholder="Search songs…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
        aria-label="Search songs"
      />

      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading songs…
        </Card>
      ) : isError ? (
        <Card className="space-y-2 py-20 text-center">
          <p className="text-sm text-danger">Couldn’t load songs.</p>
          <p className="text-xs text-dim">
            {error instanceof Error ? error.message : 'Is the API running on :3000?'}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="py-20 text-center text-sm text-muted">
          {search ? 'No songs match your search.' : 'No songs yet. Add your first song.'}
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Title</th>
                  <th className="px-5 py-3 font-semibold">Author</th>
                  <th className="px-5 py-3 font-semibold">Key</th>
                  <th className="px-5 py-3 font-semibold">BPM</th>
                  <th className="px-5 py-3 font-semibold">Tags</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((song) => (
                  <SongRow key={song.id} song={song} onDelete={() => remove.mutate(song.id)} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function SongRow({ song, onDelete }: { song: SongDto; onDelete: () => void }) {
  return (
    <tr className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover">
      <td className="px-5 py-3">
        <div className="flex items-center gap-2 font-medium">
          <Music className="size-4 text-secondary" />
          {song.title}
        </div>
        {song.ccli && <span className="text-xs text-dim">CCLI {song.ccli}</span>}
      </td>
      <td className="px-5 py-3 text-muted">{song.author ?? '—'}</td>
      <td className="px-5 py-3 text-muted">{song.defaultKey ?? '—'}</td>
      <td className="px-5 py-3 text-muted">{song.bpm ?? '—'}</td>
      <td className="px-5 py-3">
        <div className="flex flex-wrap gap-1">
          {song.tags.length === 0 ? (
            <span className="text-dim">—</span>
          ) : (
            song.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))
          )}
        </div>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end">
          <Button variant="ghost" size="icon-sm" aria-label="Delete song" onClick={onDelete}>
            <Trash2 />
          </Button>
        </div>
      </td>
    </tr>
  )
}
